import { AppLoadingSkeletonRemover } from "@components/utils/AppLoadingSkeleton";
import AppUpdateDetector from "@components/utils/AppUpdateDetector";
import ErrorFallback from "@components/utils/ErrorFallback";
import MemberSettingsLoader from "@components/utils/MemberSettingsLoader";
import ThemeColorLoader from "@components/utils/ThemeColorLoader";
import { StatsigPreloadProvider } from "@configs/statsig/preload";
import { ThemePreset } from "@configs/theme-preset";
import useCrashSessionCleanup from "@hooks/useCrashSessionCleanup";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { GoogleOAuthProvider } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import ReduxProvider from "@stores/ReduxProvider";
import "@styles/App.css";
import { initializeFirebase, isFirebaseAvailable } from "@utils/firebase";
import { ThemeProvider } from "kz-ui-sdk";
import { PropsWithChildren, useEffect } from "react";
import "react-aspect-ratio/aspect-ratio.css";
import AppContent from "./AppContent";
import "./configs/analytics/overlay-popup-tracking";
import "./configs/i18n";
// no-unused-imports-ignore
import PwaInstallInterceptor from "@components/utils/PwaInstallInterceptor/PwaInstallInterceptor";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

function App() {
  useCrashSessionCleanup();

  return (
    <ThemeProvider
      value={{
        preset: ThemePreset,
      }}
    >
      <StatsigPreloadProvider>
        <ThemeColorLoader />
        <AppLoadingSkeletonRemover />
        <MemberSettingsLoader>
          <FirebaseInitializer />
          <WithGoogleOAuthProvider>
            <AppContent />
          </WithGoogleOAuthProvider>
        </MemberSettingsLoader>
        <PwaInstallInterceptor />
      </StatsigPreloadProvider>
      <AppUpdateDetector />
    </ThemeProvider>
  );
}

/**
 * Initialize Firebase if available
 */
const FirebaseInitializer = () => {
  const { googleAuthEnabled } = useMemberSettings();

  useEffect(() => {
    if (googleAuthEnabled && isFirebaseAvailable()) {
      initializeFirebase();
    }
  }, [googleAuthEnabled]);

  return null;
};

/**
 * Conditionally wrap children in GoogleOAuthProvider if Google OAuth is enabled
 * (but not using Firebase Auth)
 */
const WithGoogleOAuthProvider = ({ children }: PropsWithChildren) => {
  const { googleAuthEnabled } = useMemberSettings();
  const useFirebase = googleAuthEnabled && isFirebaseAvailable();

  // Only use GoogleOAuthProvider if Google Auth is enabled but Firebase is not available
  if (googleAuthEnabled && !useFirebase && GOOGLE_CLIENT_ID) {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{children}</GoogleOAuthProvider>;
  }
  return children;
};

const AppWithErrorBoundary = Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }: { error: unknown; resetError: () => void }) => (
    <ErrorFallback
      error={error}
      resetError={resetError}
    />
  ),
});

/**
 * ReduxProvider wraps the error boundary (not the other way around) so the crash
 * fallback also has access to the Redux store and never throws on store access.
 */
export const EnhancedApp = () => (
  <ReduxProvider>
    <AppWithErrorBoundary />
  </ReduxProvider>
);

export default App;
